import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Stack,
  Divider,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Category as CategoryIcon,
  Layers as BundleIcon,
  AttachMoney as MoneyIcon,
  Delete as DeleteIcon,
  Store as WarehouseIcon
} from '@mui/icons-material';
import cogsService from '../../services/cogsService';
import { POPULAR_CURRENCIES } from '../../utils/currencies';
import { cogsWriteErrorMessage, skippedCostRows, skippedCostRowsWarning } from '../../utils/cogsWriteFeedback';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * MissingCOGS Component
 * Displays three sections:
 * 1. Primary SKUs (L0/L1) being sold without COGS entries
 * 2. Bundles (L2) being sold without component mappings
 * 3. Warehouse COGS Gaps — SKUs with COGS in some warehouses but not all where they ship
 */
function MissingCOGS({ brandId, onCogsAdded }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-section fetch errors. Previously all three fetches shared one try/catch, so a failure
  // in the bundle call left missingBundles at its initial [] AND skipped the warehouse-gaps
  // call entirely, while the render showed the green "none found" state for both. An error and
  // an empty result must never look the same.
  const [sectionErrors, setSectionErrors] = useState({ primary: null, bundles: null, gaps: null });
  const [success, setSuccess] = useState(null);
  // Non-blocking: a save succeeded but the server skipped some cost rows (cogs_skipped, W20).
  const [warning, setWarning] = useState(null);

  // Data
  const [missingPrimaryCOGS, setMissingPrimaryCOGS] = useState([]);
  const [missingBundles, setMissingBundles] = useState([]);
  const [warehouseGaps, setWarehouseGaps] = useState([]);

  // Filters
  const [daysBack, setDaysBack] = useState(90);
  const [minOrderCount, setMinOrderCount] = useState(1);

  // Pagination - Primary COGS
  const [primaryPage, setPrimaryPage] = useState(0);
  const [primaryRowsPerPage, setPrimaryRowsPerPage] = useState(25);
  const [primaryTotalCount, setPrimaryTotalCount] = useState(0);

  // Pagination - Bundles
  const [bundlePage, setBundlePage] = useState(0);
  const [bundleRowsPerPage, setBundleRowsPerPage] = useState(25);
  const [bundleTotalCount, setBundleTotalCount] = useState(0);

  // Pagination - Warehouse Gaps
  const [gapsPage, setGapsPage] = useState(0);
  const [gapsRowsPerPage, setGapsRowsPerPage] = useState(25);
  const [gapsTotalCount, setGapsTotalCount] = useState(0);

  // Dialog for adding COGS
  const [addCogsDialogOpen, setAddCogsDialogOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [warehouseData, setWarehouseData] = useState([]);
  const [cogsFormData, setCogsFormData] = useState({
    cogs: '',
    warehouse_code: 'FAC',
    currency: 'USD',
    notes: ''
  });

  // Dialog for converting to bundle
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [bundleComponents, setBundleComponents] = useState([{ component_sku: '', quantity: 1 }]);
  const [bundleOverhead, setBundleOverhead] = useState(0);

  // Fetch missing COGS data
  const fetchMissingData = async () => {
    if (!brandId) return;

    setLoading(true);
    setError(null);
    const errs = { primary: null, bundles: null, gaps: null };

    const common = { days_back: daysBack, min_order_count: minOrderCount };

    try {
      const primaryResponse = await cogsService.getMissingCOGS(brandId, {
        ...common, limit: primaryRowsPerPage, offset: primaryPage * primaryRowsPerPage
      });
      setMissingPrimaryCOGS(primaryResponse.data || []);
      setPrimaryTotalCount(primaryResponse.total_count || 0);
    } catch (err) {
      console.error('Error fetching missing primary COGS:', err);
      errs.primary = apiErrorMessage(err, 'Failed to fetch missing primary COGS', { requestText: false });
      setMissingPrimaryCOGS([]);
      setPrimaryTotalCount(0);
    }

    try {
      const bundleResponse = await cogsService.getMissingBundleMappings(brandId, {
        ...common, limit: bundleRowsPerPage, offset: bundlePage * bundleRowsPerPage
      });
      setMissingBundles(bundleResponse.data || []);
      setBundleTotalCount(bundleResponse.total_count || 0);
    } catch (err) {
      console.error('Error fetching missing bundle mappings:', err);
      errs.bundles = apiErrorMessage(err, 'Failed to fetch missing bundle mappings', { requestText: false });
      setMissingBundles([]);
      setBundleTotalCount(0);
    }

    try {
      const gapsResponse = await cogsService.getWarehouseCogsGaps(brandId, {
        ...common, limit: gapsRowsPerPage, offset: gapsPage * gapsRowsPerPage
      });
      setWarehouseGaps(gapsResponse.data || []);
      setGapsTotalCount(gapsResponse.total_count || 0);
    } catch (err) {
      console.error('Error fetching warehouse COGS gaps:', err);
      errs.gaps = apiErrorMessage(err, 'Failed to fetch warehouse COGS gaps', { requestText: false });
      setWarehouseGaps([]);
      setGapsTotalCount(0);
    }

    setSectionErrors(errs);
    setLoading(false);
  };

  // Initial fetch and refresh on filter/pagination changes
  useEffect(() => {
    fetchMissingData();
  }, [brandId, daysBack, minOrderCount, primaryPage, primaryRowsPerPage, bundlePage, bundleRowsPerPage, gapsPage, gapsRowsPerPage]);

  // Handle opening add COGS dialog
  const handleOpenAddCogs = async (sku) => {
    setError(null);
    setSelectedSku(sku);
    setCogsFormData({
      cogs: '',
      warehouse_code: 'FAC',
      currency: 'USD',
      notes: `Manual entry for ${sku.sku}`
    });
    setAddCogsDialogOpen(true);
    setLoadingWarehouses(true);
    setWarehouseData([]);

    // Fetch warehouses where this SKU has been shipped
    try {
      const response = await cogsService.getSkuWarehouses(brandId, sku.sku);
      if (response.warehouses && response.warehouses.length > 0) {
        // Initialize warehouse data with existing COGS or blank
        setWarehouseData(response.warehouses.map(wh => ({
          warehouse_code: wh.warehouse_code,
          order_count: wh.order_count,
          first_shipped: wh.first_shipped,
          last_shipped: wh.last_shipped,
          cogs: wh.existing_cogs || '',
          currency: wh.currency || 'USD',
          notes: wh.existing_cogs ? 'Existing COGS' : ''
        })));
      }
    } catch (err) {
      console.error('Error fetching warehouse data:', err);
      // If error, fall back to default warehouse
      setWarehouseData([{
        warehouse_code: 'FAC',
        order_count: 0,
        cogs: '',
        currency: 'USD',
        notes: ''
      }]);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  // Handle warehouse COGS field change
  const handleWarehouseCogsChange = (index, field, value) => {
    const updated = [...warehouseData];
    updated[index][field] = value;
    setWarehouseData(updated);
  };

  // Handle saving COGS
  const handleSaveCogs = async () => {
    if (!selectedSku) {
      setError('No SKU selected');
      return;
    }

    // Validate: at least one warehouse must have COGS entered
    const warehousesWithCogs = warehouseData.filter(wh => wh.cogs && parseFloat(wh.cogs) > 0);
    if (warehousesWithCogs.length === 0) {
      setError('Please enter COGS for at least one warehouse');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setWarning(null);

      // Prepare COGS array for bulk creation (one entry per warehouse)
      const cogsArray = warehousesWithCogs.map(wh => ({
        sku: selectedSku.sku,
        product_name: selectedSku.product_name,
        cogs: parseFloat(wh.cogs),
        warehouse_code: wh.warehouse_code,
        currency: wh.currency || 'USD',
        bom_level: selectedSku.bom_level,
        sku_type: selectedSku.bom_level === 0 ? 'part' : 'finished_good',
        notes: wh.notes || `Manual entry for ${selectedSku.sku} at ${wh.warehouse_code}`
      }));

      const saved = await cogsService.createOrUpdateCogs(brandId, cogsArray);

      setSuccess(`Successfully added COGS for ${selectedSku.sku} across ${warehousesWithCogs.length} warehouse(s)`);
      setWarning(skippedCostRowsWarning(skippedCostRows(saved), 'Saved'));
      setAddCogsDialogOpen(false);
      setSelectedSku(null);
      setWarehouseData([]);

      // Refresh data
      await fetchMissingData();

      // Notify parent component
      if (onCogsAdded) {
        onCogsAdded();
      }

    } catch (err) {
      console.error('Error saving COGS:', err);
      setError(cogsWriteErrorMessage(err, 'Failed to save COGS'));
    } finally {
      setLoading(false);
    }
  };

  // Handle opening convert to bundle dialog
  const handleOpenConvertToBundle = async (sku) => {
    setError(null);
    setSelectedSku(sku);
    setBundleComponents([{ component_sku: '', quantity: 1 }]);
    setBundleOverhead(0);

    // Fetch available components
    try {
      const response = await cogsService.getAvailableComponents(brandId);
      setAvailableComponents(response.data || []);
      setConvertDialogOpen(true);
    } catch (err) {
      console.error('Error fetching components:', err);
      setError('Failed to load available components');
    }
  };

  // Handle adding a component row
  const handleAddComponentRow = () => {
    setBundleComponents([...bundleComponents, { component_sku: '', quantity: 1 }]);
  };

  // Handle removing a component row
  const handleRemoveComponentRow = (index) => {
    if (bundleComponents.length > 1) {
      setBundleComponents(bundleComponents.filter((_, i) => i !== index));
    }
  };

  // Handle component SKU change
  const handleComponentChange = (index, field, value) => {
    const updated = [...bundleComponents];
    updated[index][field] = value;
    setBundleComponents(updated);
  };

  // Handle saving bundle
  const handleSaveBundle = async () => {
    if (!selectedSku) {
      setError('No SKU selected');
      return;
    }

    // Validate components
    const validComponents = bundleComponents.filter(c => c.component_sku && c.quantity > 0);
    if (validComponents.length === 0) {
      setError('Please add at least one component');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setWarning(null);

      const created = await cogsService.createBundle(brandId, {
        bundle: {
          sku: selectedSku.sku,
          product_name: selectedSku.product_name,
          warehouse_code: 'default',
          currency: 'USD',
          bom_level: 2,
          bundle_overhead: parseFloat(bundleOverhead) || 0,
          notes: `Converted from missing COGS on ${new Date().toISOString().split('T')[0]}`
        },
        components: validComponents.map(c => ({
          component_sku: c.component_sku,
          quantity: parseFloat(c.quantity),
          geography: 'ALL'
        }))
      });

      setSuccess(`Successfully converted ${selectedSku.sku} to bundle`);
      setWarning(skippedCostRowsWarning(skippedCostRows(created), 'Bundle created'));
      setConvertDialogOpen(false);
      setSelectedSku(null);

      // Refresh data
      await fetchMissingData();

      // Notify parent component
      if (onCogsAdded) {
        onCogsAdded();
      }

    } catch (err) {
      console.error('Error creating bundle:', err);
      setError(cogsWriteErrorMessage(err, 'Failed to create bundle'));
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleDaysBackChange = (event) => {
    setDaysBack(event.target.value);
    setPrimaryPage(0);
    setBundlePage(0);
    setGapsPage(0);
  };

  const handleMinOrderCountChange = (event) => {
    setMinOrderCount(event.target.value);
    setPrimaryPage(0);
    setBundlePage(0);
    setGapsPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>
              <WarningIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />
              Missing COGS Detection
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Identify products being sold without COGS data to ensure accurate profitability tracking
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Days Back</InputLabel>
                <Select value={daysBack} onChange={handleDaysBackChange} label="Days Back">
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={60}>Last 60 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                  <MenuItem value={180}>Last 180 days</MenuItem>
                  <MenuItem value={365}>Last 365 days</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Min Orders</InputLabel>
                <Select value={minOrderCount} onChange={handleMinOrderCountChange} label="Min Orders">
                  <MenuItem value={1}>1+ orders</MenuItem>
                  <MenuItem value={5}>5+ orders</MenuItem>
                  <MenuItem value={10}>10+ orders</MenuItem>
                  <MenuItem value={25}>25+ orders</MenuItem>
                  <MenuItem value={50}>50+ orders</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchMissingData}
                disabled={loading}
              >
                Refresh
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {warning && (
        <Alert severity="warning" onClose={() => setWarning(null)} sx={{ mb: 2 }}>
          {warning}
        </Alert>
      )}

      {/* Section 1: Missing Primary SKU COGS */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                <CategoryIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Missing Primary SKU COGS (L0/L1)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Finished goods and parts being sold without cost entries
              </Typography>
            </Box>
            <Chip
              label={`${primaryTotalCount} SKUs missing COGS`}
              color={primaryTotalCount > 0 ? 'error' : 'success'}
              size="small"
            />
          </Box>

          {loading && missingPrimaryCOGS.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : sectionErrors.primary ? (
            <Alert severity="error">
              {sectionErrors.primary} — this section could not be loaded, so it is not a clean bill of health.
            </Alert>
          ) : missingPrimaryCOGS.length === 0 ? (
            <Alert severity="success">
              No missing primary SKU COGS found! All products being sold have cost entries.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell align="right">Orders</TableCell>
                      <TableCell align="right">Units Sold</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell>First Sold</TableCell>
                      <TableCell>Last Sold</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missingPrimaryCOGS.map((item) => (
                      <TableRow key={item.sku} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {item.sku}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.product_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.sku_level}
                            size="small"
                            color={item.bom_level === 0 ? 'info' : 'primary'}
                          />
                        </TableCell>
                        <TableCell align="right">{item.order_count}</TableCell>
                        <TableCell align="right">{item.total_quantity_sold}</TableCell>
                        <TableCell align="right">
                          ${parseFloat(item.total_revenue || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>{item.first_sold_date}</TableCell>
                        <TableCell>{item.last_sold_date}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Add COGS">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenAddCogs(item)}
                            >
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Convert to Bundle">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleOpenConvertToBundle(item)}
                            >
                              <BundleIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={primaryTotalCount}
                page={primaryPage}
                onPageChange={(e, newPage) => setPrimaryPage(newPage)}
                rowsPerPage={primaryRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setPrimaryRowsPerPage(parseInt(e.target.value, 10));
                  setPrimaryPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Missing Bundle Mappings */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                <BundleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Missing Bundle Component Mappings (L2)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bundle products being sold without component definitions
              </Typography>
            </Box>
            <Chip
              label={`${bundleTotalCount} Bundles missing mappings`}
              color={bundleTotalCount > 0 ? 'error' : 'success'}
              size="small"
            />
          </Box>

          {loading && missingBundles.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : sectionErrors.bundles ? (
            <Alert severity="error">
              {sectionErrors.bundles} — this section could not be loaded, so it is not a clean bill of health.
            </Alert>
          ) : missingBundles.length === 0 ? (
            <Alert severity="success">
              No missing bundle mappings found! All bundles being sold have component definitions.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bundle SKU</TableCell>
                      <TableCell>Bundle Name</TableCell>
                      <TableCell>Issue Type</TableCell>
                      <TableCell>Bundle Type</TableCell>
                      <TableCell align="right">Orders</TableCell>
                      <TableCell align="right">Units Sold</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell>First Sold</TableCell>
                      <TableCell>Last Sold</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missingBundles.map((item) => (
                      <TableRow key={item.bundle_sku} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {item.bundle_sku}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.bundle_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.issue_type}
                            size="small"
                            color="warning"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.bundle_type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{item.order_count}</TableCell>
                        <TableCell align="right">{item.total_quantity_sold}</TableCell>
                        <TableCell align="right">
                          ${parseFloat(item.total_revenue || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>{item.first_sold_date}</TableCell>
                        <TableCell>{item.last_sold_date}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Define Bundle Components">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                // This would open the bundle definition dialog
                                // For now, we'll show an alert
                                alert(`Please use the Bundles tab to define components for ${item.bundle_sku}`);
                              }}
                            >
                              <BundleIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={bundleTotalCount}
                page={bundlePage}
                onPageChange={(e, newPage) => setBundlePage(newPage)}
                rowsPerPage={bundleRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setBundleRowsPerPage(parseInt(e.target.value, 10));
                  setBundlePage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Section 3: Warehouse-Specific COGS Gaps */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                <WarehouseIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Warehouse COGS Gaps
              </Typography>
              <Typography variant="body2" color="text.secondary">
                SKUs that have COGS in some warehouses but are shipping from warehouses without COGS entries
              </Typography>
            </Box>
            <Chip
              label={`${gapsTotalCount} warehouse gaps`}
              color={gapsTotalCount > 0 ? 'warning' : 'success'}
              size="small"
            />
          </Box>

          {loading && warehouseGaps.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : sectionErrors.gaps ? (
            <Alert severity="error">
              {sectionErrors.gaps} — this section could not be loaded, so it is not a clean bill of health.
            </Alert>
          ) : warehouseGaps.length === 0 ? (
            <Alert severity="success">
              No warehouse COGS gaps found! All products have COGS for every warehouse they ship from.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Missing Warehouse</TableCell>
                      <TableCell align="right">Orders</TableCell>
                      <TableCell align="right">Units Sold</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell>First Sold</TableCell>
                      <TableCell>Last Sold</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {warehouseGaps.map((item, idx) => (
                      <TableRow key={`${item.sku}-${item.missing_warehouse}-${idx}`} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {item.sku}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.product_name || '-'}</TableCell>
                        <TableCell>
                          <Chip label={item.sku_level} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.missing_warehouse}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                          {/* Amazon pool mapping (DE-FBA→EU-FBA, CA-FBA→US-FBA): when the
                              orders' raw warehouse differs from the pooled cost-book key,
                              show what the orders actually say. */}
                          {Array.isArray(item.order_warehouses)
                            && item.order_warehouses.some((w) => w !== item.missing_warehouse) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              orders say {item.order_warehouses.filter((w) => w !== item.missing_warehouse).join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{item.order_count}</TableCell>
                        <TableCell align="right">{item.total_quantity_sold}</TableCell>
                        <TableCell align="right">
                          ${parseFloat(item.total_revenue || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>{item.first_sold_date}</TableCell>
                        <TableCell>{item.last_sold_date}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Add COGS for this warehouse">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenAddCogs({
                                sku: item.sku,
                                product_name: item.product_name,
                                sku_level: item.sku_level,
                                product_level: item.product_level
                              })}
                            >
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={gapsTotalCount}
                page={gapsPage}
                onPageChange={(e, newPage) => setGapsPage(newPage)}
                rowsPerPage={gapsRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setGapsRowsPerPage(parseInt(e.target.value, 10));
                  setGapsPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Add COGS Dialog - Warehouse Specific */}
      <Dialog open={addCogsDialogOpen} onClose={() => setAddCogsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <MoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Add COGS for {selectedSku?.sku}
        </DialogTitle>
        <DialogContent>
          {/* Save errors show inside the dialog; the page-level Alert sits behind it. */}
          {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1, mb: 1 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Product Name"
              value={selectedSku?.product_name || ''}
              disabled
              fullWidth
            />
            <TextField
              label="SKU Level"
              value={selectedSku?.sku_level || ''}
              disabled
              fullWidth
            />

            <Divider sx={{ my: 2 }} />

            {loadingWarehouses ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>Loading warehouse data...</Typography>
              </Box>
            ) : warehouseData.length > 0 ? (
              <>
                <Typography variant="subtitle1" fontWeight="medium">
                  Warehouse-Specific COGS
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Enter COGS for each warehouse where this product has been shipped. Existing values are shown if available.
                </Typography>

                {warehouseData.map((warehouse, index) => (
                  <Box key={index} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2" fontWeight="bold">
                            {warehouse.warehouse_code}
                          </Typography>
                          <Chip
                            label={`${warehouse.order_count} orders`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {warehouse.first_shipped && (
                            <Typography variant="caption" color="text.secondary">
                              {warehouse.first_shipped} to {warehouse.last_shipped}
                            </Typography>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Unit COGS *"
                          type="number"
                          value={warehouse.cogs}
                          onChange={(e) => handleWarehouseCogsChange(index, 'cogs', e.target.value)}
                          fullWidth
                          size="small"
                          inputProps={{ step: 0.01, min: 0 }}
                          helperText={warehouse.cogs && parseFloat(warehouse.cogs) > 0 ? '' : 'Leave blank to skip'}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Currency</InputLabel>
                          <Select
                            value={warehouse.currency}
                            onChange={(e) => handleWarehouseCogsChange(index, 'currency', e.target.value)}
                            label="Currency"
                          >
                            {POPULAR_CURRENCIES.map((c) => (
                              <MenuItem key={c.code} value={c.code}>{c.code} · {c.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                ))}

                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Tip:</strong> You can enter COGS for multiple warehouses at once.
                    Leave blank for warehouses you don't want to update.
                  </Typography>
                </Alert>
              </>
            ) : (
              <Alert severity="warning">
                No warehouse data found for this SKU. This may indicate the SKU has no recent orders.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCogsDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCogs}
            disabled={loading || loadingWarehouses || warehouseData.filter(wh => wh.cogs && parseFloat(wh.cogs) > 0).length === 0}
          >
            Save COGS
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Bundle Dialog */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <BundleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Convert {selectedSku?.sku} to Bundle (L2)
        </DialogTitle>
        <DialogContent>
          {/* Save errors (e.g. the 409 "is an alias of" refusal) show inside the dialog. */}
          {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1, mb: 1 }}>{error}</Alert>}
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Product Name"
              value={selectedSku?.product_name || ''}
              disabled
              fullWidth
            />

            <Divider />

            <Typography variant="subtitle1" fontWeight="medium">
              Bundle Components
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select the components that make up this bundle. The bundle COGS will be automatically calculated.
            </Typography>

            {bundleComponents.map((component, index) => (
              <Grid container spacing={2} key={index} alignItems="center">
                <Grid item xs={7}>
                  <FormControl fullWidth required>
                    <InputLabel>Component SKU</InputLabel>
                    <Select
                      value={component.component_sku}
                      onChange={(e) => handleComponentChange(index, 'component_sku', e.target.value)}
                      label="Component SKU"
                    >
                      {availableComponents.map((comp) => (
                        <MenuItem key={comp.sku} value={comp.sku}>
                          {comp.sku} - {comp.product_name} (${parseFloat(comp.max_cogs).toFixed(2)})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Quantity"
                    type="number"
                    value={component.quantity}
                    onChange={(e) => handleComponentChange(index, 'quantity', e.target.value)}
                    fullWidth
                    required
                    inputProps={{ step: 1, min: 1 }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveComponentRow(index)}
                    disabled={bundleComponents.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddComponentRow}
              variant="outlined"
            >
              Add Component
            </Button>

            <Divider />

            <TextField
              label="Bundle Overhead (optional)"
              type="number"
              value={bundleOverhead}
              onChange={(e) => setBundleOverhead(e.target.value)}
              fullWidth
              inputProps={{ step: 0.01, min: 0 }}
              helperText="Additional cost for packaging, assembly, etc."
            />

            {bundleComponents.filter(c => c.component_sku).length > 0 && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Estimated Bundle COGS:</strong> $
                  {(
                    bundleComponents
                      .filter(c => c.component_sku)
                      .reduce((sum, c) => {
                        const comp = availableComponents.find(ac => ac.sku === c.component_sku);
                        return sum + (comp ? parseFloat(comp.max_cogs) * parseFloat(c.quantity || 0) : 0);
                      }, 0) + parseFloat(bundleOverhead || 0)
                  ).toFixed(2)}
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveBundle}
            disabled={loading || bundleComponents.filter(c => c.component_sku).length === 0}
            startIcon={<BundleIcon />}
          >
            Create Bundle
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MissingCOGS;
